// =============================================================================
// Auth-domain TypeScript types -- mirror backend Pydantic schemas.
// =============================================================================
// Source of truth: backend/app/schemas/{auth.py,user.py,common.py} +
// docs/VandieZ_Build_03_API_Spec.md Sec 2 (Auth Endpoints).
//
// Hand-maintained for now. Once the FastAPI OpenAPI export pipeline is
// wired (Sprint 7+), these will be regenerated by openapi-typescript so
// they can't drift from the backend.
// =============================================================================

export type Role = "shipper" | "driver" | "owner" | "agent" | "admin";
export type OtpPurpose = "signup" | "login" | "change_phone";
export type Language = "ta" | "en" | "hi" | "te" | "ml" | "kn";

export interface OtpRequestPayload {
  phone: string;
  purpose: OtpPurpose;
}

export interface OtpRequestResponse {
  otp_session_id: string;
  expires_in_seconds: number;
  masked_phone: string;
}

export interface OtpVerifyPayload {
  otp_session_id: string;
  otp_code: string;
  primary_role?: Role;
  full_name?: string;
  preferred_language?: Language;
  device_fingerprint?: string;
  referral_code?: string;
}

export interface UserRead {
  id: string;
  phone: string;
  full_name: string;
  primary_role: Role;
  roles: Role[];
  preferred_language: Language;
  status: string;
  kyc_tier: string;
  email: string | null;
  profile_photo_url: string | null;
  // ... many more fields; only the ones we render today.
}

export interface OtpVerifyResponse {
  user: UserRead;
  new_user: boolean;
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type?: string;
}

export interface RefreshResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type?: string;
}

// =============================================================================
// Problem-Detail error shape (RFC 7807; emitted by backend handlers in
// app/main.py + app/core/exceptions.py).
// =============================================================================
export interface ProblemDetail {
  type?: string;
  title?: string;
  status: number;
  detail: string;
  code?: string;
  // VALIDATION_ERROR responses include this:
  errors?: {
    loc: (string | number)[];
    msg: string;
    type: string;
  }[];
}

/** Thrown by the api client when the backend returns 4xx/5xx. */
export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string | undefined;
  public readonly problem: ProblemDetail;

  constructor(problem: ProblemDetail) {
    super(problem.detail);
    this.name = "ApiError";
    this.status = problem.status;
    this.code = problem.code;
    this.problem = problem;
  }
}
